import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { IconPort } from "@/assets/icons/geist/IconPort";
import { withGitpodSize } from "@/assets/icons/utils";
import { OpenInEditorButton } from "@/components/environments/OpenInEditorButton";
import { OpenPortPopover } from "@/components/environments/OpenPortForm";
import { WithPopover } from "@/components/environments/WithPopover";
import { Button } from "@/components/flexkit/Button";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { TrackLocations } from "@/hooks/use-segment";
import { useEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { EnvironmentActionsDropdown } from "@/routes/environments/details/EnvironmentActionsDropdown";
import { canOpen } from "@/routes/environments/phase";
import IconConsoleSimple from "central-icons/IconConsoleSimple";
import { useEffect, useMemo, useState, type FC } from "react";

const IconSSH = withGitpodSize(IconConsoleSimple);

export type ActionBarProps = {
    environmentId: string;
    withOpenEditor?: boolean;
};

export const ActionBar: FC<ActionBarProps> = ({ environmentId, withOpenEditor }) => {
    const { data: environment } = useEnvironment(environmentId);

    return (
        <div data-testid="action-bar" translate="no" className="grow self-stretch">
            {environment && <Island environment={environment} withOpenEditor={withOpenEditor} />}
        </div>
    );
};

const Island: FC<
    PropsWithClassName & {
        environment: PlainEnvironment;
        withOpenEditor?: boolean;
    }
> = ({ environment, className, withOpenEditor = true }) => {
    const { selectedEditor } = useGetEditorForEnv(environment.id);

    const [open, setOpen] = useState(false);
    const openable = useMemo(() => {
        return !!environment && canOpen(environment);
    }, [environment]);

    useEffect(() => {
        setOpen((current) => {
            if (current !== openable) {
                return openable;
            }
            return current;
        });
    }, [openable]);

    useEffect(() => {
        setOpen(!!environment && canOpen(environment));
    }, [environment]);

    return (
        <div
            data-testid="action-bar-island"
            className={cn("flex flex-wrap items-stretch justify-center gap-2 @xs:justify-end", className)}
        >
            {open && !window.isWebview && withOpenEditor && <OpenInEditorButton environment={environment} />}
            {open && (
                <WithPopover
                    align="end"
                    button={
                        <Button size="xs" LeadingIcon={IconSSH} variant="outline" data-testid="open-ssh">
                            SSH
                        </Button>
                    }
                    data-track-location={TrackLocations.EnvironmentSSHFormPopover}
                >
                    <SSHForm environment={environment} />
                </WithPopover>
            )}
            {open && (
                <OpenPortPopover
                    button={
                        <Button size="xs" LeadingIcon={IconPort} variant="outline" data-testid="open-public-ports">
                            Open Port
                        </Button>
                    }
                    environmentId={environment.id}
                />
            )}
            <EnvironmentActionsDropdown
                environment={environment}
                defaultEditor={selectedEditor}
                button={
                    <Button
                        variant="outline"
                        size="xs"
                        data-testid="open-more-actions"
                        aria-label="More actions"
                        LeadingIcon={IconEllipsisHorizontal}
                        // Note: We have to explicitly set padding here as DropdownActions sets a padding that we don't want
                        className="size-auto px-2 py-1.5"
                    />
                }
            />
        </div>
    );
};

type SSHFormProps = {
    environment: PlainEnvironment;
};
const SSHForm: FC<SSHFormProps> = ({ environment }) => {
    return (
        <div className="flex w-[500px] flex-col gap-4 p-5">
            <div>
                <Heading2>Connect via SSH</Heading2>
                <Text muted className="text-base">
                    How to connect via SSH from your local machine using the CLI
                </Text>
            </div>
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <p className="font-bold">Setup SSH host config</p>
                    <CopyableInput value={"gitpod env ssh-config"} data-tracking-id="copy-ssh-config-command" />
                </div>
                <div className="flex flex-col gap-1">
                    <p className="font-bold">Connect to dev container</p>
                    <CopyableInput
                        value={`ssh ${environment.id}.gitpod.environment`}
                        data-tracking-id="copy-ssh-connect-command"
                    />
                </div>
            </div>
            <div className="-mx-1 my-1 h-px bg-content-tertiary/20" />
            <p className="text-sm text-content-secondary">Documentation</p>
            <div className="flex flex-col gap-2">
                <div>
                    <ExternalLink href="https://ona.com/docs/ona/integrations/cli">Install the CLI</ExternalLink>
                </div>
                <div>
                    <ExternalLink href="https://ona.com/docs/ona/integrations/cli#ssh-configuration">
                        SSH Configuration
                    </ExternalLink>
                </div>
            </div>
        </div>
    );
};
